import {
  ComponentProps,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ConfirmButton } from 'src/components/Buttons';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { EventMapContextProvider } from 'src/contexts/EventMapContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { CancellableFormFooter } from 'src/modals/common';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from 'src/modals/common/EventEntityHeader';
import { ModalBody, ModalFooter } from 'src/modals/Modal';
import { ContentId } from 'src/utils/constants/contentId';
import { isDatePassedHours } from 'src/utils/dateTimeUtils';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Event,
  Feature,
  ListingClient,
  ListingMergeExclusivityResult,
  ListingMetrics,
  MergedListingSectionTypeResolutionResult,
  MergedSectionTypeResolution,
  MergeListingsInput,
  VenueMapSectionType,
} from 'src/WebApiController';

import { Summary } from '../common/Summary';
import {
  ModalBodyDataContainer,
  ModalBodyHeaderContainer,
} from '../Modal/Modal.styled';
import { isEqualIgnoringOrder } from './BulkMergeListings/BulkMergeListings.utils';
import * as styles from './MergeListings.css';
import { MergeListingsBody } from './MergeListingsBody';
import { MergeSectionTypePrompt } from './MergeSectionTypePrompt';
import { useMergeExclusivityWarningMessage } from './useMergeExclusivityWarningMessage';
import { useMergeGroupedListingAlertDialog } from './useMergeGroupedListingsDialog';

export const MergeListingsModal = ({ event }: { event: Event }) => {
  const methods = useForm<MergeListingsInput>({
    defaultValues: {
      listingIds: [],
    },
  });

  return (
    // POS-7295: provide the venue map so the seller can map a section when no source has a section type.
    <EventMapContextProvider event={event} venueMapOnly>
      <FormProvider {...methods}>
        <MergeListingsModalContent {...methods} event={event} />
      </FormProvider>
    </EventMapContextProvider>
  );
};

const MergeListingsModalContent = ({
  event,
  formState,
  handleSubmit,
  setError,
  watch,
  getValues,
}: { event: Event } & Omit<
  ComponentProps<typeof FormProvider<MergeListingsInput, unknown>>,
  'children'
>) => {
  const { closeModal } = useContext(ModalContext);
  const { activeAccountWebClientConfig } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  // POS-7295: kept separate from `isLoading` because `isLoading` swaps the modal body for a spinner,
  // which would unmount the listings table and drop the row selection while the seller is choosing.
  const [isResolvingSectionType, setIsResolvingSectionType] = useState(false);
  const [sectionPrompt, setSectionPrompt] =
    useState<MergedListingSectionTypeResolutionResult | null>(null);
  const [pendingFormData, setPendingFormData] =
    useState<MergeListingsInput | null>(null);
  // POS-7295: the section/source the seller picked for the pending merge. Picking a value must not
  // submit by itself — the seller confirms by clicking Merge again.
  const [selectedSectionTypeId, setSelectedSectionTypeId] =
    useState<VenueMapSectionType | null>(null);
  // POS-7295: which action the pending section pick belongs to — the table selection ("Merge") or a
  // "Merge Suggested" group. Used to place the picker and to know when the pick is still applicable.
  const [sectionPromptSource, setSectionPromptSource] = useState<
    'table' | 'suggestion' | null
  >(null);
  // Set by the action that starts a merge, read when the preflight surfaces a prompt. A ref (not state)
  // because it must be visible to the async `mergeListings` without triggering a re-render.
  const pendingMergeSourceRef = useRef<'table' | 'suggestion'>('table');
  // POS-9467: the exclusivity warning is informational and rendered inline alongside the other merge
  // warnings. The first Merge click surfaces it; the next click (the seller having seen it) merges.
  const [isResolvingExclusivity, setIsResolvingExclusivity] = useState(false);
  const [exclusivityWarning, setExclusivityWarning] =
    useState<ListingMergeExclusivityResult | null>(null);
  // POS-9467: which selection the inline exclusivity warning was already shown for, so the warning is
  // only surfaced once per selection and a fresh selection warns again.
  const exclusivityWarningSelectionRef = useRef<string | null>(null);
  const { isSubmitting } = formState;

  const { showErrorDialog } = useClientLogContext();

  // POS-7295: the section type disambiguation only applies when the GA merged-max-display feature is on.
  const hasGaMergedMaxDisplayFeature = useUserHasFeature(
    Feature.GeneralAdmissionMaxDisplayQuantity
  );

  // POS-9467: the merge exclusivity warning is behind its own flag.
  const hasMergeExclusivityWarningFeature = useUserHasFeature(
    Feature.ListingMergeExclusivityWarning
  );

  const {
    eventsTransformed,
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { refreshMetrics } = useCatalogMetricsContext<ListingMetrics>();

  const listingIds = watch('listingIds');

  // POS-7295: a section pick is only valid for the merge it was made for. Recompute whether the pending
  // prompt still applies to the action the seller is looking at:
  // - "table" merges are valid while the selected listings are unchanged;
  // - "suggestion" merges are valid while the table has no selection (clicking Merge Suggested clears it).
  const isSectionPromptActive = useMemo(() => {
    if (sectionPrompt == null || pendingFormData == null) {
      return false;
    }
    if (sectionPromptSource === 'table') {
      return isEqualIgnoringOrder(pendingFormData.listingIds, listingIds);
    }
    if (sectionPromptSource === 'suggestion') {
      return (listingIds ?? []).length === 0;
    }
    return false;
  }, [sectionPrompt, pendingFormData, sectionPromptSource, listingIds]);

  // Drop a pick that no longer applies so it can never be reused for a different merge.
  useEffect(() => {
    if (!isSectionPromptActive) {
      setSectionPrompt(null);
      setPendingFormData(null);
      setSelectedSectionTypeId(null);
      setSectionPromptSource(null);
    }
  }, [isSectionPromptActive]);

  const eventData = useMemo(
    () => eventsTransformed?.find((ev) => ev.event.virtId === event.virtId),
    [eventsTransformed, event.virtId]
  );

  const needToMergeTwoOrMoreListingsError = useContent(
    ContentId.NeedToMergeToTwoOrMoreListings
  );

  const getListingsFromListingIds = useCallback(
    (listingIds: number[]) => {
      return eventData?.entities?.listings?.filter((listing) =>
        listingIds.includes(listing.id)
      );
    },
    [eventData]
  );

  const isSelectedListingsFromDifferentAccounts = useMemo(() => {
    const listings = getListingsFromListingIds(listingIds);
    const accountIds = Array.from(
      new Set(listings?.map((listing) => listing.accId) || [])
    );
    return accountIds.length > 1;
  }, [getListingsFromListingIds, listingIds]);

  const mergeListings = useCallback(
    async (formData: MergeListingsInput, closeModalAfterMerge?: boolean) => {
      if (formData.listingIds.length < 2) {
        setError('listingIds', {
          message: needToMergeTwoOrMoreListingsError,
        });
        return;
      }

      // POS-7295: starting a new merge invalidates any section pick made for a previous one. Only an
      // explicit confirmation (which carries mergedSectionTypeId) is allowed to reuse the pick.
      if (formData.mergedSectionTypeId == null) {
        setSectionPrompt(null);
        setPendingFormData(null);
        setSelectedSectionTypeId(null);
        setSectionPromptSource(null);
      }

      return await tryInvokeApi(
        async () => {
          const listingClient = new ListingClient(activeAccountWebClientConfig);

          // POS-7295: if the source listings are ambiguous (GA vs non-GA, or no known section type),
          // ask the seller to disambiguate before merging. Uses its own loading flag so the modal body
          // (and the selected listings) is not replaced by a spinner while the seller chooses.
          if (
            hasGaMergedMaxDisplayFeature &&
            formData.mergedSectionTypeId == null
          ) {
            setIsResolvingSectionType(true);
            let resolution:
              | MergedListingSectionTypeResolutionResult
              | undefined;
            try {
              const resolutions =
                await listingClient.getMergedListingSectionTypeResolutions([
                  formData.listingIds,
                ]);
              resolution = resolutions?.[0];
            } finally {
              setIsResolvingSectionType(false);
            }

            if (
              resolution &&
              resolution.resolution !== MergedSectionTypeResolution.Resolved
            ) {
              setPendingFormData(formData);
              setSectionPrompt(resolution);
              setSelectedSectionTypeId(null);
              setSectionPromptSource(pendingMergeSourceRef.current);
              return;
            }
          }

          // POS-9467: warn when the sources mix fan, broker and non-B2B inventory, because the merged
          // listing takes on the most restrictive of them. Informational — the warning renders inline
          // and the merge runs on the next click, so the merge itself is never blocked.
          if (hasMergeExclusivityWarningFeature) {
            setIsResolvingExclusivity(true);
            let warning: ListingMergeExclusivityResult | undefined;
            try {
              const warnings = await listingClient.getMergeExclusivityWarnings([
                formData.listingIds,
              ]);
              warning = warnings?.[0];
            } finally {
              setIsResolvingExclusivity(false);
            }

            // Only hold the merge back the first time for a given selection: once the seller has seen
            // the warning, the next click on the same selection goes through. Re-evaluated on every new
            // selection so a warning from a previous selection never lingers.
            const selectionKey = [...formData.listingIds]
              .sort((a, b) => a - b)
              .join(',');
            if (exclusivityWarningSelectionRef.current !== selectionKey) {
              exclusivityWarningSelectionRef.current = selectionKey;
              setExclusivityWarning(
                warning?.hasMixedSourceClasses ? warning : null
              );
              if (warning?.hasMixedSourceClasses) {
                return;
              }
            }
          }

          // Only now is this an actual merge; `isLoading` may safely swap the body for a spinner.
          setIsLoading(true);

          const result = await listingClient.mergeListingsWithListingId(
            formData.listingIds[0],
            formData
          );

          if (isSuccess(result)) {
            await refreshExpandedListItems();
            refreshMetrics?.();
            if (closeModalAfterMerge) {
              closeModal(true);
            }
          } else {
            showErrorDialog('ListingClient.mergeListings', result, {
              trackErrorData: formData,
            });
          }
        },
        (error) => {
          showErrorDialog('ListingClient.mergeListings', error, {
            trackErrorData: formData,
          });
        },
        () => setIsLoading(false)
      );
    },
    [
      activeAccountWebClientConfig,
      closeModal,
      hasGaMergedMaxDisplayFeature,
      hasMergeExclusivityWarningFeature,
      needToMergeTwoOrMoreListingsError,
      refreshExpandedListItems,
      refreshMetrics,
      setError,
      showErrorDialog,
    ]
  );

  const {
    setAlerts,
    warningDialog,
    mergeGroupedListingHookProps,
    onSubmit,
    AlertDialog,
  } = useMergeGroupedListingAlertDialog(handleSubmit, mergeListings);

  const onSubmitHandler = useCallback(() => {
    // POS-7295: when the picker is shown for the current table selection, Merge confirms the pending
    // merge with the picked value instead of re-running the disambiguation preflight.
    if (
      isSectionPromptActive &&
      pendingFormData &&
      selectedSectionTypeId != null
    ) {
      mergeListings(
        { ...pendingFormData, mergedSectionTypeId: selectedSectionTypeId },
        true
      );
      return;
    }

    // Otherwise this is (or has become) a merge of the table selection.
    pendingMergeSourceRef.current = 'table';

    const formData = getValues();
    const hasListingFromGroup = formData.listingIds.some(
      (formDataListingId) => {
        const listing = eventData?.entities?.listings?.find(
          (listing) => listing.id === formDataListingId
        );

        // If no listing is found in eventData.listings then that means it's in a group
        if (!listing) {
          return true;
        }

        return false;
      }
    );

    if (
      hasListingFromGroup &&
      isDatePassedHours(mergeGroupedListingHookProps.lastTimeStamp, 1)
    ) {
      setAlerts([mergeGroupedListingHookProps]);
      warningDialog.launchDialog();
    } else {
      handleSubmit(onSubmit)();
    }
  }, [
    getValues,
    mergeGroupedListingHookProps,
    eventData?.entities?.listings,
    setAlerts,
    warningDialog,
    handleSubmit,
    onSubmit,
    mergeListings,
    isSectionPromptActive,
    pendingFormData,
    selectedSectionTypeId,
  ]);

  const onMergeSuggestions = useCallback(
    (listingIds: number[]) => {
      // POS-7295: once the seller has picked a section type for this suggestion, "Merge Suggested"
      // confirms the pending merge with that pick. Without this it would re-run the disambiguation
      // preflight, which resets the pick and shows the picker again.
      if (
        isSectionPromptActive &&
        sectionPromptSource === 'suggestion' &&
        pendingFormData != null &&
        selectedSectionTypeId != null &&
        isEqualIgnoringOrder(pendingFormData.listingIds, listingIds)
      ) {
        mergeListings(
          { ...pendingFormData, mergedSectionTypeId: selectedSectionTypeId },
          true
        );
        return;
      }

      pendingMergeSourceRef.current = 'suggestion';
      mergeListings({ listingIds, mergedSectionTypeId: null });
    },
    [
      mergeListings,
      isSectionPromptActive,
      sectionPromptSource,
      pendingFormData,
      selectedSectionTypeId,
    ]
  );

  // POS-7295: only records the pick; the merge is confirmed via the Merge button (onSubmitHandler).
  const onSelectSectionType = useCallback(
    (sectionType: VenueMapSectionType) => {
      setSelectedSectionTypeId(sectionType);
    },
    []
  );

  // POS-9467: inline copy naming the exclusivity classes the merge mixes, e.g. "You are merging fan
  // and broker inventory".
  const exclusivityWarningMsg = useMergeExclusivityWarningMessage(
    exclusivityWarning?.sourceClasses
  );

  const activeSectionPrompt = isSectionPromptActive ? sectionPrompt : null;
  const isTableSectionPrompt =
    activeSectionPrompt != null && sectionPromptSource === 'table';

  // POS-7295: the picker sits above the suggestions for a table merge, but stays below the "Merge
  // Suggested" buttons for a suggestion merge so the two flows are easy to tell apart.
  const sectionPromptNode = activeSectionPrompt ? (
    <>
      <div>
        <Content
          id={
            activeSectionPrompt.resolution ===
            MergedSectionTypeResolution.RequiresSourceSelection
              ? ContentId.MergeListings_SectionTypeDifference
              : ContentId.MergeListings_SectionMapping
          }
        />
      </div>
      <MergeSectionTypePrompt
        resolution={activeSectionPrompt.resolution}
        candidates={activeSectionPrompt.candidates}
        selectedSectionTypeId={selectedSectionTypeId}
        onSelect={onSelectSectionType}
      />
    </>
  ) : null;

  return (
    <>
      <CancellableFormHeader disabled={isLoading || isSubmitting}>
        <ConnectedEventEntityHeader
          title={<Content id={ContentId.MergeListings} />}
        />
      </CancellableFormHeader>
      <ModalBody>
        {isLoading || eventData?.entities?.listings == null ? (
          <PosSpinner />
        ) : (
          eventData && (
            <>
              <ModalBodyHeaderContainer>
                <Summary event={eventData.event} />
              </ModalBodyHeaderContainer>
              <ModalBodyDataContainer>
                <MergeListingsBody
                  {...eventData}
                  disabled={
                    isLoading ||
                    isSubmitting ||
                    isResolvingSectionType ||
                    isResolvingExclusivity
                  }
                  onMergeSuggestions={onMergeSuggestions}
                  hasGaMergedMaxDisplayFeature={hasGaMergedMaxDisplayFeature}
                  sectionPromptNode={sectionPromptNode}
                  sectionPromptAboveSuggestions={isTableSectionPrompt}
                  pendingSectionListingIds={
                    activeSectionPrompt != null &&
                    sectionPromptSource === 'suggestion'
                      ? pendingFormData?.listingIds
                      : undefined
                  }
                  sectionTypeSelected={selectedSectionTypeId != null}
                  footer={
                    listingIds?.length ? (
                      <div className={styles.footerButtonsContainer}>
                        <ConfirmButton
                          onClick={onSubmitHandler}
                          disabled={
                            isLoading ||
                            isSubmitting ||
                            isResolvingSectionType ||
                            isResolvingExclusivity ||
                            isSelectedListingsFromDifferentAccounts ||
                            (isSectionPromptActive &&
                              selectedSectionTypeId == null)
                          }
                          textContentId={ContentId.Merge}
                        />
                      </div>
                    ) : undefined
                  }
                />
                {AlertDialog}
                {exclusivityWarningMsg && (
                  <WarningMessage message={exclusivityWarningMsg} />
                )}
              </ModalBodyDataContainer>
            </>
          )
        )}
      </ModalBody>
      <ModalFooter>
        <CancellableFormFooter
          disabled={isLoading || isSubmitting}
        ></CancellableFormFooter>
      </ModalFooter>
    </>
  );
};
